import { DeepPartial } from 'redux';
import { PromotedFeatures } from './store';

const STORAGE_KEY = 'featuresToPromote_data';

export const getStoredFeaturesToPromote = (): Omit<PromotedFeatures, 'isLoading'> | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    console.error('Error getting from session storage');
    return null;
  }
};

export const setStoredFeaturesToPromote = (data: Omit<PromotedFeatures, 'isLoading'>): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    console.error('Error setting to session storage');
  }
};

export const getPathFromConfig = <T>(config: DeepPartial<T>): string | null => {
  if (!config || typeof config !== 'object') return null;

  const findLeafPath = (obj: DeepPartial<T>, path: string[] = []): string | null => {
    for (const [key, value] of Object.entries(obj)) {
      const currentPath = [...path, key];

      // Return path for any leaf value (boolean, string, number, etc.)
      if (value !== null && value !== undefined && typeof value !== 'object') {
        return currentPath.join('.');
      }

      if (value && typeof value === 'object') {
        const result = findLeafPath(value, currentPath);
        if (result) return result;
      }
    }
    return null;
  };

  return findLeafPath(config);
};

const getValueFromPath = (obj: any, path: string) => {
  if (!obj || typeof obj !== 'object') return undefined;

  const pathParts = path.split('.');
  let current: any = obj;

  for (const part of pathParts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return undefined;
    }
  }

  return current;
};

export const shouldPromoteFeature = <T>(features: T[], path: string, expectedValue: any): boolean => {
  return !features.every((feature) => {
    const currentValue = getValueFromPath(feature, path);

    if (expectedValue !== undefined) {
      return currentValue === expectedValue;
    }

    return Boolean(currentValue);
  });
};

export const checkFeaturesForPromotion = <T>(
  features: T[],
  config: { [key: string]: { condition: DeepPartial<T>; title: string; description: string; docsLink: string } }
): string[] => {
  const promotedFeatures: string[] = [];

  for (const [featureKey, featureConfig] of Object.entries(config)) {
    const path = getPathFromConfig(featureConfig.condition);

    if (path) {
      const expectedValue = getValueFromPath(featureConfig.condition, path);
      if (shouldPromoteFeature(features, path, expectedValue)) {
        promotedFeatures.push(featureKey);
      }
    }
  }

  return promotedFeatures;
};
